import React from "react";
import { oneDriveService } from "@services/OneDriveService";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import { ExternalFileData } from "./ExternalFilePreview";
import { DriveFolder } from "@shared/interfaces/CloudFiles";

const useOneDrive = ({
  projectId,
  setOneDriveFolders,
  setOneDriveFoldersLoading,
  setExternalFilesDataOneDrive,
  setLoadingExternalFilesOneDrive,
  setGDriveFoldersLoading,
}: {
  projectId: string | undefined;
  setOneDriveFolders: React.Dispatch<React.SetStateAction<DriveFolder[]>>;
  setOneDriveFoldersLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setExternalFilesDataOneDrive: React.Dispatch<
    React.SetStateAction<ExternalFileData>
  >;
  setLoadingExternalFilesOneDrive: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  setGDriveFoldersLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const fetchFoldersOneDrive = async () => {
    if (!projectId) return;

    try {
      setGDriveFoldersLoading(true);
      const data = await oneDriveService.getProjectFolders(projectId);
      setOneDriveFolders(data.folders);
    } catch (error) {
      console.error("Failed to fetch folders:", error);
    } finally {
      setOneDriveFoldersLoading(false);
    }
  };

  const fetchOneDriveFolderFiles = async (
    projectId: string,
    folderId: string
  ): Promise<ServerFile[]> => {
    try {
      const data = await oneDriveService.getFolderFiles(projectId, folderId);
      return data.files as ServerFile[];
    } catch (error) {
      console.error("Failed to fetch folder files:", error);
      return [];
    }
  };

  const triggerOnedriveFoldersRefresh = () => {
    fetchFoldersOneDrive();
  };

  const loadExternalFilesOneDrive = async () => {
    if (!projectId) return;

    setLoadingExternalFilesOneDrive(true);
    try {
      const fetched = await oneDriveService.getExternalFiles(projectId, 0, 100);
      setExternalFilesDataOneDrive(fetched);
    } catch (err) {
      console.error("Failed to load external files:", err);
    } finally {
      setLoadingExternalFilesOneDrive(false);
    }
  };

  return {
    fetchFoldersOneDrive,
    fetchOneDriveFolderFiles,
    loadExternalFilesOneDrive,
    triggerOnedriveFoldersRefresh,
  };
};

export default useOneDrive;
